import Icon from "../../components/Icon"
import { getCustomerInitials } from "./utbillsHelpers"
import CompanyBillRow from "./CompanyBillRow"
import { describeCompanyStatus, describeRequest, describeWaitingBills } from "./autopayApprovalDisplay"

const TONE_TEXT = {
  amber: "text-amber-700",
  emerald: "text-emerald-700",
  slate: "text-slate-500",
}

// Same switch as the auto-invoice list in Customers, sized for a thumb.
function AutopaySwitch({ checked, disabled, label, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
        checked ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  )
}

// One company that bills the customer: its auto-pay switch and every bill from
// it that is still unpaid. For a company that is not approved these wait for
// the customer; for an approved one they stay only while the wallet is short or
// the customer objected to the amount.
function AutopayCompanyRow({ company, walletBalance, busy, onToggle, onPay, onObject }) {
  const status = describeCompanyStatus(company)
  const waitingText = describeWaitingBills(company)
  const requestText = describeRequest(company)
  const bills = Array.isArray(company.bills) ? company.bills : company.waitingBills || []
  const hasWaiting = company.waitingBills?.length > 0
  const hasBills = bills.length > 0
  const walletReady = !company.locked && Number(walletBalance || 0) > 0
  const someCanPay = bills.some((bill) => bill.canPay !== false)

  return (
    <li className={`px-4 py-3 ${hasWaiting ? "bg-amber-50/60" : ""}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-black text-slate-600">
          {getCustomerInitials(company.providerName)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">{company.providerName}</span>
          <span className={`block text-[11px] font-semibold ${TONE_TEXT[status.tone] || TONE_TEXT.slate}`}>
            {status.label}
          </span>
          {(waitingText || requestText) && (
            <span className="block text-[11px] text-slate-500">
              {[waitingText, requestText].filter(Boolean).join(" · ")}
            </span>
          )}
        </span>
        {company.canChange ? (
          <AutopaySwitch
            checked={Boolean(company.approved)}
            disabled={busy}
            label={`Auto-pay for ${company.providerName}`}
            onChange={(next) => onToggle(company, next)}
          />
        ) : (
          <Icon name="lock-fill" size="0.85rem" className="shrink-0 text-slate-400" aria-hidden="true" />
        )}
      </div>

      {company.locked && (
        <p className="mt-2 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-600">
          This company is locked, so its bills cannot be paid right now.
        </p>
      )}

      {hasBills && (
        <div className="mt-2 sm:ml-12">
          <ul className="space-y-1.5">
            {bills.map((bill) => (
              <CompanyBillRow
                key={bill.invoiceId}
                bill={bill}
                companyName={company.providerName}
                walletReady={walletReady}
                busy={busy}
                onPay={onPay}
                onObject={onObject}
              />
            ))}
          </ul>
          {!company.locked && someCanPay && !(Number(walletBalance || 0) > 0) && (
            <p className="mt-1.5 text-[11px] text-slate-500">Top up your wallet to pay these bills.</p>
          )}
        </div>
      )}
    </li>
  )
}

export default AutopayCompanyRow
